import React, { useReducer } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box, DatePickerDate, DateRangeVariant, Popover, SelectedTimeRange } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import FilterActions from '@/components/Filter/FilterActions';

import { DateFilterListItem, DateSelectorProps } from '../constants';
import DateList from '../DateList';
import { dateSelectionContainer } from '../styles';
import { DateFilterPosition } from '../types';
import { ActionType, State } from './types';
import { getSelectedOption, reducer } from './utils';

const DateSelector = (props: DateSelectorProps) => {
  const { onDone, onCancel, options, selectedOption, position, children, open } = props;
  const initialState: State = {
    startTime: props.startTime ?? new Date(),
    endTime: props.endTime,
    selectedItem: getSelectedOption(selectedOption, props.startTime, props.endTime),
    isCalendarOpen: false,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const popoverPosition = position === DateFilterPosition.Left ? 'bottom-start' : 'bottom-end';
  const { isMobile } = useScreen();

  const onFilterSave = (
    startTime: DatePickerDate,
    endTime: DatePickerDate,
    selectedItem?: DateFilterListItem,
  ) => {
    if (!startTime || !endTime) {
      dispatch({ type: ActionType.ClearDate });
    }
    onDone(startTime, endTime, selectedItem);
    dispatch({ type: ActionType.SetCalendar, payload: { value: false } });
  };

  if (state.isCalendarOpen) {
    return (
      <DatePicker
        open={open}
        pickerProps={{
          variant: DateRangeVariant.DateRange,
          selectedRange: { startTime: state.startTime, endTime: state.endTime },
          onChange: (selectedRange: SelectedTimeRange) =>
            onFilterSave(
              selectedRange?.startTime,
              selectedRange?.endTime,
              options[options.length - 1],
            ),
        }}
        position={popoverPosition}
        onOpenChange={onCancel}>
        {children}
      </DatePicker>
    );
  }

  return (
    <Popover open={open} onOpenChange={onCancel}>
      <Popover.Button>{children}</Popover.Button>
      <Popover.Content
        bottomSheetProps={{
          enableContentPanningGesture: false,
        }}
        position={popoverPosition}
        renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
        <Box className={cn(dateSelectionContainer, 'xs:!w-full')}>
          <DateList
            list={options}
            selectedItem={state.selectedItem}
            onClick={(value: DateFilterListItem) => {
              dispatch({ type: ActionType.DateListItemClick, payload: { value } });
            }}
          />
          <FilterActions
            onClear={() => {
              dispatch({ type: ActionType.ClearList });
            }}
            onSave={() => onFilterSave(state.startTime, state.endTime, state.selectedItem)}
          />
        </Box>
      </Popover.Content>
    </Popover>
  );
};

export default DateSelector;
